import React, { useState, useRef, useEffect } from 'react';
import { X, FileText, PenTool, Check, AlertCircle } from 'lucide-react';

interface SubscriptionAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSigned: (data: SignatureData) => void;
  userEmail?: string;
  userName?: string;
  mode?: 'signup' | 'update'; // 'signup' for registration, 'update' for existing users
}

interface SignatureData {
  signerName: string;
  signerEmail: string;
  signerTitle: string;
  signatureData: string;
  agreementVersion: string;
}

const SubscriptionAgreementModal: React.FC<SubscriptionAgreementModalProps> = ({
  isOpen,
  onClose,
  onSigned,
  userEmail = '',
  userName = '',
  mode = 'signup'
}) => {
  const [agreementText, setAgreementText] = useState('');
  const [agreementVersion, setAgreementVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'read' | 'sign'>('read');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Signature state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signerName, setSignerName] = useState(userName);
  const [signerEmail, setSignerEmail] = useState(userEmail);
  const [signerTitle, setSignerTitle] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const agreementRef = useRef<HTMLDivElement>(null);

  // Fetch agreement text
  useEffect(() => {
    if (isOpen) {
      fetchAgreement();
    }
  }, [isOpen]);

  // Update signer info when props change
  useEffect(() => {
    setSignerName(userName);
    setSignerEmail(userEmail);
  }, [userName, userEmail]);

  const fetchAgreement = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/subscription-agreement/current');
      const data = await response.json();

      if (data.success) {
        setAgreementText(data.agreementText);
        setAgreementVersion(data.version);
      } else {
        setError('Failed to load agreement');
      }
    } catch (err) {
      setError('Failed to load agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle scroll to track if user has read the agreement
  const handleScroll = () => {
    if (agreementRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = agreementRef.current;
      // Consider "read" if scrolled to within 50px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    }
  };

  // Canvas drawing functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();

    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleProceedToSign = () => {
    if (!hasScrolledToBottom) {
      alert('Please scroll to the bottom of the agreement to continue.');
      return;
    }
    setStep('sign');
  };

  const handleSubmitSignature = async () => {
    if (!signerName.trim()) {
      alert('Please enter your full name');
      return;
    }
    if (!signerEmail.trim()) {
      alert('Please enter your email address');
      return;
    }
    if (!hasSignature) {
      alert('Please provide your signature');
      return;
    }
    if (!acceptTerms) {
      alert('Please accept the terms to continue');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const signatureData = canvas.toDataURL('image/png');

    setSubmitting(true);

    try {
      onSigned({
        signerName: signerName.trim(),
        signerEmail: signerEmail.trim(),
        signerTitle: signerTitle.trim(),
        signatureData,
        agreementVersion
      });
    } catch (err) {
      alert('Failed to process signature. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold">
              {step === 'read' ? 'Master SaaS and Services Agreement' : 'Sign Agreement'}
            </h2>
          </div>
          {mode !== 'signup' && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600">{error}</p>
                <button
                  onClick={fetchAgreement}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : step === 'read' ? (
            <div className="flex flex-col h-full">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Please read the entire agreement.</strong> Scroll to the bottom to enable the "Proceed to Sign" button.
                </p>
              </div>

              <div
                ref={agreementRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto bg-gray-50 border rounded-lg p-6 font-mono text-sm whitespace-pre-wrap"
                style={{ maxHeight: 'calc(90vh - 280px)' }}
              >
                {agreementText}
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Agreement Version: <strong>{agreementVersion}</strong>
                </p>
                <button
                  onClick={handleProceedToSign}
                  disabled={!hasScrolledToBottom}
                  className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
                    hasScrolledToBottom
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <PenTool className="h-5 w-5" />
                  Proceed to Sign
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Scrollable form content */}
              <div className="flex-1 overflow-y-auto space-y-4 pr-2" style={{ maxHeight: 'calc(95vh - 200px)' }}>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <Check className="h-4 w-4 inline mr-1" />
                    You have reviewed the Master SaaS and Services Agreement (Version {agreementVersion}).
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your full legal name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={signerEmail}
                      onChange={(e) => setSignerEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title/Position (Optional)
                  </label>
                  <input
                    type="text"
                    value={signerTitle}
                    onChange={(e) => setSignerTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Owner, CEO, Manager"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      <PenTool className="h-4 w-4 inline mr-1" />
                      Digital Signature <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Clear
                    </button>
                  </div>
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={100}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    className="w-full border-2 border-gray-300 rounded-lg cursor-crosshair bg-white"
                    style={{ touchAction: 'none', height: '100px' }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Sign above using your mouse or touchscreen
                  </p>
                </div>

                <div className="bg-gray-50 border rounded-lg p-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      I have read and agree to the Master SaaS and Services Agreement. I understand that by signing below,
                      I am entering into a legally binding contract with ProBiz Pay LLC. I confirm that I have the authority
                      to enter into this agreement on behalf of the Customer.
                    </span>
                  </label>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-semibold mb-1">No long-term commitment</p>
                    <p>
                      You can cancel your subscription anytime from <strong>Admin &amp; Settings</strong> —
                      no cancellation fees, no questions asked. Signing this agreement only sets the
                      terms of service while you&apos;re using ProBiz Pay.
                    </p>
                  </div>
                </div>
              </div>

              {/* Fixed footer with buttons */}
              <div className="flex items-center justify-between pt-4 mt-4 border-t bg-white">
                <button
                  onClick={() => setStep('read')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back to Agreement
                </button>
                <button
                  onClick={handleSubmitSignature}
                  disabled={submitting || !hasSignature || !acceptTerms || !signerName.trim() || !signerEmail.trim()}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Check className="h-5 w-5" />
                  {submitting ? 'Signing...' : 'Sign Agreement'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAgreementModal;
